
import { supabase } from "@/integrations/supabase/client";
import { NotificationType } from "./types";

// Check if a user has notifications enabled
export const shouldNotifyUser = async (
  userId: string, 
  notificationType: NotificationType
): Promise<boolean> => {
  try {
    console.log(`Checking notification preferences for user ${userId} and type ${notificationType}`);
    
    // First validate the user ID
    if (!userId || userId.trim() === '') {
      console.error("Invalid user ID provided to shouldNotifyUser:", userId);
      return false;
    }
    
    // Query the database for user preferences
    const { data, error } = await supabase
      .from("profiles")
      .select("email_notifications, notify_swap_offers, notify_swap_responses, notify_messages, daily_digest_email, push_notifications, push_messages")
      .eq("id", userId)
      .single();
      
    if (error) {
      console.error("Error checking notification preferences:", error);
      
      // Handle the specific error where notify_messages column doesn't exist
      if (error.message && error.message.includes("notify_messages")) {
        console.warn("notify_messages column may not exist yet. Defaulting to true for message notifications.");
        // Default to allowing notifications if column doesn't exist yet
        if (notificationType === "messages") {
          return true;
        }
      }
      
      return false;
    }
    
    if (!data) {
      console.error(`No profile found for user ${userId}`);
      return false;
    }
    
    console.log(`User preferences for ${userId}:`, data);
    
    // First check if email notifications are enabled at all
    if (!data.email_notifications) {
      console.log(`User ${userId} has disabled all email notifications`);
      return false;
    }
    
    // Then check the specific notification type
    if (notificationType === "swap_offers") {
      return !!data.notify_swap_offers;
    } else if (notificationType === "swap_responses") {
      return !!data.notify_swap_responses;
    } else if (notificationType === "messages") {
      // If notify_messages doesn't exist in the data yet, default to true
      return data.notify_messages === undefined ? true : !!data.notify_messages;
    } else if (notificationType === "daily_digest") {
      return !!data.daily_digest_email;
    }
    
    console.log(`Unknown notification type: ${notificationType}`);
    return false;
  } catch (error) {
    console.error("Exception in shouldNotifyUser:", error);
    return false;
  }
};

// Check if a user has push notifications enabled
export const shouldSendPushNotification = async (
  userId: string, 
  notificationType: NotificationType
): Promise<boolean> => {
  try {
    console.log(`Checking push notification preferences for user ${userId} and type ${notificationType}`);
    
    // First validate the user ID
    if (!userId || userId.trim() === '') {
      console.error("Invalid user ID provided to shouldSendPushNotification:", userId);
      return false;
    }
    
    // Query the database for user push preferences
    const { data, error } = await supabase
      .from("profiles")
      .select("push_notifications, push_messages, push_swap_offers, push_swap_responses, push_weekly_digest, push_new_reagents, push_reagent_requests")
      .eq("id", userId)
      .single();
      
    if (error) {
      console.error("Error checking push notification preferences:", error);
      return false;
    }
    
    if (!data) {
      console.error(`No profile found for user ${userId}`);
      return false;
    }
    
    console.log(`User push preferences for ${userId}:`, data);
    
    // First check if push notifications are enabled at all
    if (!data.push_notifications) {
      console.log(`User ${userId} has disabled all push notifications`);
      return false;
    }
    
    // Then check the specific notification type
    if (notificationType === "messages") {
      return !!data.push_messages;
    } else if (notificationType === "swap_offers") {
      return !!data.push_swap_offers;
    } else if (notificationType === "swap_responses") {
      return !!data.push_swap_responses;
    } else if (notificationType === "daily_digest") {
      return !!data.push_weekly_digest;
    } else if (notificationType === "new_reagents") {
      return data.push_new_reagents !== false; // Default to true if not set
    } else if (notificationType === "reagent_requests") {
      return data.push_reagent_requests !== false; // Default to true if not set
    }
    
    console.log(`Unknown push notification type: ${notificationType}`);
    return false;
  } catch (error) {
    console.error("Exception in shouldSendPushNotification:", error);
    return false;
  }
};
